import axios from 'axios';
import type { TFunction } from 'i18next';

/**
 * SPR-1054: the sign/edit/enable/disable-approval endpoints resolve "which representative is this"
 * by matching the login email against Donex Contact records (see
 * IContactsService.GetRepresentativeContactByEmailAsync) - there's no representative concept in
 * the frontend's user model to gate these actions on beforehand, so both failure modes are real,
 * expected outcomes to show inline rather than leaking as a raw HTTP status string:
 * - 403: the matched Contact exists but isn't a representative of this fund (or has view-only
 *   permission) - ContactsService.GetRepresentativeContactByEmailAsync / NotFundRepresentativeException.
 * - 404: no Contact anywhere has this login's email - ContactNotFoundException. This is the
 *   pre-existing gap where a new-viewer-client login has no linked Donex Contact at all.
 * Any other status falls back to the generic per-action error label with the server's own message
 * when it sent one (ASP.NET's BadRequest(string)/NotFound(string) bodies are plain strings), never
 * axios's own "Request failed with status code NNN" text.
 */
export const getRepresentativeActionErrorMessage = (err: unknown, t: TFunction, genericErrorKey: string): string => {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 403) {
      return t('representativeOnlyActionError');
    }
    if (err.response?.status === 404) {
      return t('representativeNotFoundError');
    }
    if (typeof err.response?.data === 'string' && err.response.data.trim()) {
      return `${t(genericErrorKey)}: ${err.response.data}`;
    }
  }

  return t(genericErrorKey);
};
